import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarPage = () => {
  const [meetings, setMeetings] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [meetingsRes, followupsRes] = await Promise.all([
        axios.get(`${API}/meetings`),
        axios.get(`${API}/followups`)
      ]);
      setMeetings(meetingsRes.data);
      setFollowups(followupsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getMeetingsForDate = (date) => {
    return meetings.filter(m => isSameDay(new Date(m.date), date));
  };

  const getFollowupsForDate = (date) => {
    return followups.filter(f => f.reminder_date && isSameDay(new Date(f.reminder_date), date));
  };

  const selectedDateMeetings = selectedDate ? getMeetingsForDate(selectedDate) : [];
  const selectedDateFollowups = selectedDate ? getFollowupsForDate(selectedDate) : [];

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading calendar...</div>;
  }

  return (
    <div className="f500-page space-y-8" data-testid="calendar-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Calendar</h1>
        <p className="text-muted-foreground mt-2 text-lg leading-relaxed">
          Visual timeline of your meetings and follow-ups
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-sm border border-border lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-medium">{format(currentMonth, 'MMMM yyyy')}</h2>
            <div className="flex gap-2">
              <Button aria-label="Previous"
                variant="outline"
                size="icon"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="rounded-sm"
                data-testid="prev-month"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button aria-label="Next"
                variant="outline"
                size="icon"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="rounded-sm"
                data-testid="next-month"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-muted-foreground p-2">
                {day}
              </div>
            ))}
            {Array.from({ length: monthStart.getDay() }).map((_, i) => (
              <div key={`empty-${i}`} className="p-2" />
            ))}
            {daysInMonth.map(date => {
              const dayMeetings = getMeetingsForDate(date);
              const dayFollowups = getFollowupsForDate(date);
              const hasEvents = dayMeetings.length > 0 || dayFollowups.length > 0;
              const isSelected = selectedDate && isSameDay(date, selectedDate);

              return (
                <motion.button
                  key={date.toISOString()}
                  onClick={() => setSelectedDate(date)}
                  className={`p-3 rounded-sm border transition-all ${
                    isSelected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : hasEvents
                      ? 'border-accent bg-accent/10 hover:bg-accent/20'
                      : 'border-border hover:bg-muted'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`calendar-day-${format(date, 'yyyy-MM-dd')}`}
                >
                  <div className="text-sm font-medium">{format(date, 'd')}</div>
                  {hasEvents && (
                    <div className="flex gap-1 mt-1 justify-center">
                      {dayMeetings.length > 0 && (
                        <div className="w-1 h-1 rounded-full bg-current" />
                      )}
                      {dayFollowups.length > 0 && (
                        <div className="w-1 h-1 rounded-full bg-current" />
                      )}
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </Card>

        <Card className="p-6 rounded-sm border border-border">
          <h2 className="text-2xl font-medium mb-6">
            {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'Select a date'}
          </h2>

          {selectedDate ? (
            <div className="space-y-4">
              {selectedDateMeetings.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    Meetings ({selectedDateMeetings.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedDateMeetings.map(meeting => (
                      <div
                        key={meeting.id}
                        className="p-3 rounded-sm border border-border bg-muted/50"
                        data-testid={`calendar-meeting-${meeting.id}`}
                      >
                        <p className="font-medium text-sm">{meeting.title}</p>
                        {meeting.client_name && (
                          <p className="text-xs text-muted-foreground mt-1">{meeting.client_name}</p>
                        )}
                        <p className="text-xs font-mono text-muted-foreground mt-1">
                          {format(new Date(meeting.date), 'h:mm a')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedDateFollowups.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    Follow-ups ({selectedDateFollowups.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedDateFollowups.map(followup => (
                      <div
                        key={followup.id}
                        className="p-3 rounded-sm border border-border bg-muted/50"
                        data-testid={`calendar-followup-${followup.id}`}
                      >
                        <p className="font-medium text-sm">{followup.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">{followup.client_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedDateMeetings.length === 0 && selectedDateFollowups.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No events on this date</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Select a date to view events</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CalendarPage;